import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { SplitText } from '@/components/generals/split-text'

const HERO_COPY = 'Notes from the studio on process, craft, and the work behind the work.'

/* The reference indents the opening line with a run of 27 hard spaces
   (U+00A0) rather than a text-indent, so the justified block keeps its exact
   measure while the first line starts inset. */
const NBSP = String.fromCharCode(160)
const HERO_INDENT = NBSP.repeat(27)

export const JournalHero: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    /* The headline is the one run on the page that is NOT split per glyph: it
       rises as a single masked block, which is why it lands heavier than the
       copy underneath it. */
    useGsap(() => {
        const heading = scope.current?.querySelector<HTMLElement>('[data-journal-heading]')

        if (!heading) {
            return
        }

        if (prefersReducedMotion()) {
            gsap.set(heading, { yPercent: 0 })

            return
        }

        gsap.fromTo(heading,
            { yPercent: 110 },
            {
                yPercent: 0,
                duration: 1.45,
                delay: 0.35,
                ease: 'noma'
            }
        )
    }, scope, [])

    return (
        <section
            id='journal-hero'
            ref={scope}
            className='mx-auto w-full max-w-[1920px] px-5 pb-12 pt-10 lg:px-[33px] lg:pb-[72px] lg:pt-[88px]'
        >
            <div className='grid gap-5 lg:grid-cols-4'>
                {/* Column one stays empty on desktop so the headline sits
                    offset against the rail, and collapses away on mobile. */}
                <div className='hidden lg:block' aria-hidden='true' />

                <div className='flex flex-col gap-9 lg:col-span-3 lg:gap-14'>
                    <div className='overflow-hidden'>
                        <h1
                            data-journal-heading
                            className='t-display block pb-[0.08em] text-ink'
                            style={{
                                fontSize: 'clamp(45px, 9vw, 140px)',
                                transform: 'translateY(110%)'
                            }}
                        >
                            our Journal.
                        </h1>
                    </div>

                    <p
                        className='t-cond leading-[1.18] text-ash'
                        style={{ fontSize: 'clamp(19px, 1.6vw, 23px)', textAlign: 'justify' }}
                    >
                        <span className='sr-only'>
                            {HERO_COPY}
                        </span>

                        <span aria-hidden='true'>
                            <span className='select-none'>
                                {HERO_INDENT}
                            </span>

                            <SplitText
                                as='span'
                                className='inline'
                                text={HERO_COPY}
                                mode='chars'
                                trigger='mount'
                                delay={0.62}
                                stagger={0.009}
                                duration={0.95}
                            />
                        </span>
                    </p>
                </div>
            </div>
        </section>
    )
}
